import React from 'react';
import styled from 'styled-components';
import { Spacer } from 'src/common/Spacer';
import { Poppins } from 'src/common/Poppins';
import { ArrowRightSvg, InfoCircleSvg } from 'src/assets/dynamicSvg';
import { useRiskIntel } from '../useRiskIntel';
import { useLoadingAction } from '../useLoadingAction';
import { useScrollOnReveal } from '../useScrollOnReveal';
import { SectionHeader } from '../comps/SectionHeader';
import { FetchingState } from '../comps/FetchingState';
import { PrimaryButton } from '../comps/SuggestionButtons';
import { ImpactSelectStep } from './ImpactSelectStep';
import { ImpactFormulaSection } from './ImpactFormulaSection';

const MissingWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.dropZoneErrBg};
  border: 1px solid ${({ theme }) => theme.colors.dropZoneErrBorder};
  border-radius: 8px;
`;

export const ConsequencesStep: React.FC = () => {
  const { session, busy, proceedToReview } = useRiskIntel();
  const { loadingKey, run } = useLoadingAction();
  const mining = session?.status === 'mining_impacts';
  // Impacts are fetched async after frequency; loader until they land.
  const hasImpacts = (session?.impacts?.length ?? 0) > 0;
  const selectedWithFormula = (session?.impacts ?? []).filter((impact) => impact.selected && impact.formula);
  const hasFormulas = selectedWithFormula.length > 0;
  const canProceed = !!session?.impactsAccepted && session?.status !== 'review';
  // An unpopulated variable leaves the Result uncomputable; allow proceed and warn rather than block.
  const allResolved =
    hasFormulas &&
    selectedWithFormula.every(
      (impact) => Number.isFinite(impact.formula?.computedLower) && Number.isFinite(impact.formula?.computedUpper),
    );

  // Scroll to the loader, then push again once formulas land; and to Proceed when it unlocks.
  const formulaRef = useScrollOnReveal(mining ? 'mining' : hasFormulas ? 'loaded' : false);
  const proceedRef = useScrollOnReveal(canProceed);

  return (
    <div>
      <SectionHeader>Consequences</SectionHeader>
      <Spacer $px={24} />
      {!hasImpacts ? (
        <FetchingState
          title="Fetching relevant impacts"
          subtitle="The risk intel is identifying the impacts most relevant to your scenario."
        />
      ) : (
        <ImpactSelectStep />
      )}
      <div ref={formulaRef}>
        {/* Loader during ANY quant (incl. re-quant for extra impacts), and hide the list while it runs —
            otherwise a re-quant shows a partial list (existing formulas, new impact still missing). */}
        {mining && hasImpacts && (
          <>
            <Spacer $px={32} />
            <FetchingState
              title="Fetching Impact Formula"
              subtitle="The risk intel is fetching impact formula that match your exact risk consequences."
            />
          </>
        )}
        {!mining && hasFormulas && (
          <>
            <Spacer $px={32} />
            <ImpactFormulaSection />
          </>
        )}
      </div>
      {canProceed && (
        <div ref={proceedRef}>
          {!allResolved && (
            <>
              <Spacer $px={32} />
              <MissingWarning>
                <InfoCircleSvg size={20} color="#E13D3D" style={{ flexShrink: 0, marginTop: 1 }} />
                <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
                  Some impacts are still missing a variable value, so the calculated Result will be incomplete. You can
                  proceed and populate them later, or add the values above to complete it now.
                </Poppins>
              </MissingWarning>
            </>
          )}
          <Spacer $px={32} />
          <PrimaryButton
            label="Proceed to review"
            icon={<ArrowRightSvg size={22} />}
            fullWidth
            loading={loadingKey === 'proceed'}
            disabled={busy}
            onClick={() => run('proceed', () => proceedToReview())}
          />
        </div>
      )}
    </div>
  );
};
